import React, { useState, useEffect, useCallback } from "react"
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Empty,
  Spin,
  Button,
  Tooltip,
  Statistic,
  Flex,
  Grid,
  Divider,
  Popconfirm,
  message,
} from "antd"
import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  WalletOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  NumberOutlined,
  CreditCardOutlined,
  SyncOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons"
import NumberFormat from "react-number-format"
import {
  getBankTransactionsApi,
  reconcileIrkTransactionsApi,
} from "helpers/backend_helper"
import { PersianDatePickerInline } from "components/Common/PersianDatePickerInline"
import { formatFaDateTime } from "helpers/utilities"

const { Title, Text } = Typography
const { CheckableTag } = Tag
const { useBreakpoint } = Grid

// Mobile Card Component for each transaction
const TransactionMobileCard = ({
  record,
  canReconcile,
  onReconcile,
  reconcilingId,
}) => {
  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderRadius: 12,
        border: record.confirm ? "1px solid #b7eb8f" : "1px solid #ffa39e",
        background: record.confirm ? "#f6ffed" : "#fff2f0",
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        {/* Header Row */}
        <Flex justify="space-between" align="center">
          <Space>
            <BankOutlined style={{ color: "#1890ff" }} />
            <Text strong>{record.gatewayTypeName || "درگاه پرداخت"}</Text>
          </Space>
          <Tag
            color={record.confirm ? "success" : "error"}
            icon={
              record.confirm ? <CheckCircleOutlined /> : <CloseCircleOutlined />
            }
          >
            {record.confirm ? "تایید شده" : "تایید نشده"}
          </Tag>
        </Flex>

        <Divider style={{ margin: "8px 0" }} />

        {/* Amount */}
        <Flex justify="space-between" align="center">
          <Text type="secondary">مبلغ:</Text>
          <Text strong style={{ fontSize: 16, color: "#52c41a" }}>
            <NumberFormat
              value={record.amount}
              displayType="text"
              thousandSeparator
              suffix=" ریال"
            />
          </Text>
        </Flex>

        {/* Date */}
        <Flex justify="space-between" align="center">
          <Text type="secondary">تاریخ:</Text>
          <Text>{formatFaDateTime(record.julianLocalDateTime)}</Text>
        </Flex>

        {/* Reference Number */}
        <Flex justify="space-between" align="center">
          <Text type="secondary">شناسه پرداخت:</Text>
          <Text
            copyable={{ tooltips: ["کپی", "کپی شد!"] }}
            style={{ fontSize: 12 }}
          >
            {record.orderId || "-"}
          </Text>
        </Flex>

        {/* File Number */}
        {record.fileNumber && (
          <Flex justify="space-between" align="center">
            <Text type="secondary">شماره پرونده:</Text>
            <Tag color="blue">{record.fileNumber}</Tag>
          </Flex>
        )}

        {/* Landlord Name */}
        {record.landlordName && (
          <Flex justify="space-between" align="center">
            <Text type="secondary">مالک:</Text>
            <Text>{record.landlordName}</Text>
          </Flex>
        )}

        {/* Description */}
        {record.description && (
          <Flex justify="space-between" align="start">
            <Text type="secondary">توضیحات:</Text>
            <Text style={{ textAlign: "left", maxWidth: "60%" }}>
              {record.description}
            </Text>
          </Flex>
        )}

        {/* Reference Number */}
        <Flex justify="space-between" align="center">
          <Text type="secondary">شماره پیگیری:</Text>
          <Text
            copyable={{ tooltips: ["کپی", "کپی شد!"] }}
            style={{ fontSize: 12 }}
          >
            {record.retrievalReferenceNumber || "-"}
          </Text>
        </Flex>

        {/* Reconcile action */}
        {canReconcile && !record.confirm && (
          <Popconfirm
            title="تطبیق تراکنش"
            description="آیا از تطبیق این تراکنش اطمینان دارید؟"
            onConfirm={() => onReconcile(record)}
            okText="بله، تطبیق شود"
            cancelText="انصراف"
            okButtonProps={{ danger: false }}
          >
            <Button
              type="primary"
              ghost
              icon={<SyncOutlined spin={reconcilingId === record.id} />}
              loading={reconcilingId === record.id}
              block
              style={{ borderRadius: 8, marginTop: 4 }}
            >
              تطبیق تراکنش
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Card>
  )
}

const BankTransactions = ({ userId = null, canReconcile = false }) => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [summary, setSummary] = useState({
    totalAmount: 0,
    confirmedCount: 0,
    totalCount: 0,
  })
  const [reconcilingId, setReconcilingId] = useState(null)

  // Date filter state
  const [filterDate, setFilterDate] = useState({
    gregorian: null,
    persian: null,
    julianDay: null,
  })
  const [statusFilter, setStatusFilter] = useState(null) // null = all, true = confirmed, false = failed
  const statusOptions = [
    { label: "همه", value: null, color: "default" },
    { label: "تایید شده", value: true, color: "success" },
    { label: "ناموفق", value: false, color: "error" },
  ]

  // Fetch transactions from API
  const fetchTransactions = useCallback(
    async (page = 1, pageSize = 10, dateFilter = null) => {
      setLoading(true)
      try {
        const queryParams = new URLSearchParams({
          Page: page,
          PageSize: pageSize,
        })

        // Add date filter if provided
        if (dateFilter?.gregorian) {
          queryParams.append("JulianLocalDateTime", dateFilter.gregorian)
        }

        // Add userId filter if provided (Accountant role viewing specific executor)
        if (userId) {
          queryParams.append("userId", userId)
        }

        const response = await getBankTransactionsApi(queryParams.toString())

        if (response) {
          setTransactions(response.data || [])
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize,
            total: response.totalItems || 0,
          }))

          // Calculate summary
          const data = response.data || []
          setSummary({
            totalAmount: data.reduce((sum, t) => sum + (t.amount || 0), 0),
            confirmedCount: data.filter(t => t.confirm).length,
            totalCount: data.length,
          })
        }
      } catch (error) {
        console.error("Error fetching bank transactions:", error)
      } finally {
        setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    fetchTransactions(1, pagination.pageSize, filterDate)
  }, [])

  // Re-fetch when userId changes
  useEffect(() => {
    fetchTransactions(1, pagination.pageSize, filterDate)
  }, [userId])

  // Handle table pagination change
  const handleTableChange = newPagination => {
    fetchTransactions(newPagination.current, newPagination.pageSize, filterDate)
  }

  // Handle refresh
  const handleRefresh = () => {
    fetchTransactions(pagination.current, pagination.pageSize, filterDate)
  }

  // Handle date filter change
  const handleDateFilterChange = dateData => {
    setFilterDate(dateData)
  }

  // Handle apply filter
  const handleApplyFilter = () => {
    fetchTransactions(1, pagination.pageSize, filterDate)
  }

  // Handle clear filter
  const handleClearFilter = () => {
    setFilterDate({
      gregorian: null,
      persian: null,
      julianDay: null,
    })
    setStatusFilter(null)
    fetchTransactions(1, pagination.pageSize, null)
  }

  // Handle reconcile transaction
  const handleReconcile = async record => {
    setReconcilingId(record.id)
    try {
      const response = await reconcileIrkTransactionsApi({
        date: record.julianLocalDateTime,
        btId: record.id,
      })
      const updated = Array.isArray(response) ? response : [response]
      if (updated.length > 0) {
        setTransactions(prev =>
          prev.map(t => {
            const match = updated.find(u => u.id === t.id)
            return match ? { ...t, ...match } : t
          })
        )
        // Recalculate summary
        setTransactions(prev => {
          setSummary({
            totalAmount: prev.reduce((sum, t) => sum + (t.amount || 0), 0),
            confirmedCount: prev.filter(t => t.confirm).length,
            totalCount: prev.length,
          })
          return prev
        })
        message.success("تطبیق تراکنش با موفقیت انجام شد")
      }
    } catch (error) {
      console.error("Error reconciling transaction:", error)
      message.error(error)
    } finally {
      setReconcilingId(null)
    }
  }

  // Filter transactions locally by status and search text
  const filteredTransactions = transactions.filter(t => {
    if (statusFilter !== null && t.confirm !== statusFilter) return false
    return true
  })

  // Desktop columns
  const columns = [
    {
      title: (
        <Space>
          <CalendarOutlined />
          تاریخ
        </Space>
      ),
      dataIndex: "solarLocalDateTime",
      key: "solarLocalDateTime",
      width: 140,
      render: text => <Text style={{ fontSize: 12 }}>{text}</Text>,
    },
    {
      title: (
        <Space>
          <WalletOutlined />
          مبلغ (ریال)
        </Space>
      ),
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: amount => (
        <Text strong style={{ color: "#52c41a" }}>
          <NumberFormat value={amount} displayType="text" thousandSeparator />
        </Text>
      ),
    },
    {
      title: (
        <Space>
          <CreditCardOutlined />
          شناسه پرداخت
        </Space>
      ),
      dataIndex: "orderId",
      key: "orderId",
      width: 140,
      render: text => (
        <Text
          copyable={text ? { tooltips: ["کپی", "کپی شد!"] } : false}
          style={{ fontSize: 11 }}
        >
          {text || "-"}
        </Text>
      ),
    },
    {
      title: "وضعیت",
      dataIndex: "confirm",
      key: "confirm",
      width: 100,
      align: "center",
      render: confirm => (
        <Tag
          color={confirm ? "success" : "error"}
          icon={confirm ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {confirm ? "تایید" : "ناموفق"}
        </Tag>
      ),
    },
    {
      title: (
        <Space>
          <NumberOutlined />
          شماره پرونده
        </Space>
      ),
      dataIndex: "fileNumber",
      key: "fileNumber",
      width: 120,
      render: fileNumber =>
        fileNumber ? <Tag color="blue">{fileNumber}</Tag> : "-",
    },
    {
      title: (
        <Space>
          <UserOutlined />
          مجری/مالک
        </Space>
      ),
      dataIndex: "executorFullName",
      key: "executorFullName",
      width: 140,
      ellipsis: true,
      render: name => name || "-",
    },
    {
      title: (
        <Space>
          <FileTextOutlined />
          توضیحات
        </Space>
      ),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <Text style={{ fontSize: 12 }}>{text || "-"}</Text>
        </Tooltip>
      ),
    },
    {
      title: (
        <Space>
          <BankOutlined />
          درگاه
        </Space>
      ),
      dataIndex: "gatewayTypeName",
      key: "gatewayTypeName",
      width: 100,
      render: text => <Tag color="purple">{text || "نامشخص"}</Tag>,
    },
    {
      title: (
        <Space>
          <CreditCardOutlined />
          شماره پیگیری
        </Space>
      ),
      dataIndex: "retrievalReferenceNumber",
      key: "retrievalReferenceNumber",
      width: 140,
      render: text => (
        <Text
          copyable={text ? { tooltips: ["کپی", "کپی شد!"] } : false}
          style={{ fontSize: 11 }}
        >
          {text || "-"}
        </Text>
      ),
    },
    ...(canReconcile
      ? [
          {
            title: "عملیات",
            key: "action",
            width: 110,
            align: "center",
            render: (_, record) =>
              !record.confirm ? (
                <Popconfirm
                  title="تطبیق تراکنش"
                  description="آیا از تطبیق این تراکنش اطمینان دارید؟"
                  onConfirm={() => handleReconcile(record)}
                  okText="بله، تطبیق شود"
                  cancelText="انصراف"
                >
                  <Button
                    type="link"
                    icon={<SyncOutlined spin={reconcilingId === record.id} />}
                    loading={reconcilingId === record.id}
                    size="small"
                  >
                    تطبیق
                  </Button>
                </Popconfirm>
              ) : (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  —
                </Text>
              ),
          },
        ]
      : []),
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {/* Summary Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  کل تراکنش‌ها
                </Text>
              }
              value={pagination.total}
              valueStyle={{ color: "#fff", fontSize: isMobile ? 20 : 24 }}
              suffix={
                <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                  مورد
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              border: "none",
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  تایید شده این صفحه
                </Text>
              }
              value={summary.confirmedCount}
              valueStyle={{ color: "#fff", fontSize: isMobile ? 20 : 24 }}
              suffix={
                <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                  از {summary.totalCount}
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              border: "none",
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  مجموع این صفحه
                </Text>
              }
              value={summary.totalAmount}
              formatter={value => (
                <NumberFormat
                  value={value}
                  displayType="text"
                  thousandSeparator
                />
              )}
              valueStyle={{ color: "#fff", fontSize: isMobile ? 18 : 22 }}
              suffix={
                <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                  ریال
                </Text>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Search and Actions */}
      <Card
        size="small"
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: isMobile ? 12 : 16 } }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={24} md={8} lg={6}>
            <Flex align="center" gap={8}>
              <CalendarOutlined style={{ color: "#1890ff", fontSize: 16 }} />
              <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                تاریخ:
              </Text>
              <PersianDatePickerInline
                setPersianDate={handleDateFilterChange}
                setDefault={false}
                defaultDate={filterDate?.gregorian}
                isPortal={true}
              />
            </Flex>
          </Col>
          <Col xs={24} sm={24} md={10} lg={10}>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Text type="secondary">وضعیت تراکنش:</Text>
              <Space wrap size="small">
                {statusOptions.map(option => (
                  <CheckableTag
                    key={option.label}
                    checked={statusFilter === option.value}
                    onChange={() =>
                      setStatusFilter(prev =>
                        prev === option.value ? null : option.value
                      )
                    }
                    style={{ borderRadius: 999 }}
                  >
                    {option.label}
                  </CheckableTag>
                ))}
              </Space>
            </Space>
          </Col>
          <Col xs={24} sm={24} md={6} lg={8}>
            <Flex
              gap={8}
              justify={isMobile ? "center" : "flex-end"}
              wrap="wrap"
            >
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleApplyFilter}
                loading={loading}
              >
                اعمال فیلتر
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleClearFilter}>
                پاک کردن
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                {!isMobile && "بروزرسانی"}
              </Button>
            </Flex>
          </Col>
        </Row>
        {filterDate?.persian && (
          <Flex style={{ marginTop: 12 }} gap={8} align="center">
            <Text type="secondary">فیلتر فعال:</Text>
            <Tag color="blue" closable onClose={handleClearFilter}>
              تاریخ: {filterDate.persian}
            </Tag>
          </Flex>
        )}
        {statusFilter !== null && (
          <Flex style={{ marginTop: 12 }} gap={8} align="center">
            <Text type="secondary">وضعیت:</Text>
            <Tag
              color={statusFilter ? "success" : "error"}
              closable
              onClose={() => setStatusFilter(null)}
            >
              {statusFilter ? "تایید شده" : "ناموفق"}
            </Tag>
          </Flex>
        )}
      </Card>

      {/* Transactions List */}
      <Spin spinning={loading}>
        {isMobile ? (
          // Mobile View - Cards
          <div>
            {filteredTransactions.length > 0 ? (
              <>
                {filteredTransactions.map(transaction => (
                  <TransactionMobileCard
                    key={transaction.id}
                    record={transaction}
                    canReconcile={canReconcile}
                    onReconcile={handleReconcile}
                    reconcilingId={reconcilingId}
                  />
                ))}
                {/* Mobile Pagination */}
                <Flex justify="center" style={{ marginTop: 16 }}>
                  <Button
                    disabled={pagination.current === 1}
                    onClick={() =>
                      fetchTransactions(
                        pagination.current - 1,
                        pagination.pageSize,
                        filterDate
                      )
                    }
                    style={{ marginLeft: 8 }}
                  >
                    قبلی
                  </Button>
                  <Text style={{ margin: "0 16px", alignSelf: "center" }}>
                    صفحه {pagination.current} از{" "}
                    {Math.ceil(pagination.total / pagination.pageSize) || 1}
                  </Text>
                  <Button
                    disabled={
                      pagination.current >=
                      Math.ceil(pagination.total / pagination.pageSize)
                    }
                    onClick={() =>
                      fetchTransactions(
                        pagination.current + 1,
                        pagination.pageSize,
                        filterDate
                      )
                    }
                    style={{ marginRight: 8 }}
                  >
                    بعدی
                  </Button>
                </Flex>
              </>
            ) : (
              <Card style={{ borderRadius: 12 }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="تراکنشی یافت نشد"
                />
              </Card>
            )}
          </div>
        ) : (
          // Desktop View - Table
          <Card
            style={{ borderRadius: 12, overflow: "hidden" }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              columns={columns}
              dataSource={filteredTransactions}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} از ${total} تراکنش`,
                pageSizeOptions: ["10", "20", "50"],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1000 }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="تراکنشی یافت نشد"
                  />
                ),
              }}
            />
          </Card>
        )}
      </Spin>
    </Space>
  )
}

export default BankTransactions
